import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MarkdownStreamingContext } from './CodeBlock';
import {
  answerLabel,
  CHOICE_MARKS,
  isInchatQuizEnabled,
  isShortAnswerCorrect,
  parseQuizSpec,
  quizSpecToMarkdown,
} from '../../utils/chatQuizSpec';
import { trackChatUxEvent } from '../../utils/eventTracker';
import QuizCopyButton from './QuizCopyButton';
import QuizPlainView from './QuizPlainView';
import './InchatQuiz.css';

const isChatPage = () => typeof window !== 'undefined' && window.location.pathname.startsWith('/chat');

const OX_OPTIONS = [
  { value: 'O', label: 'O', hint: '맞아요' },
  { value: 'X', label: 'X', hint: '아니에요' },
];

/** 한 문항의 답 기록 → 맞았는지 */
const isCorrect = (item, record) => {
  if (!record) return false;
  if (record.override) return true;
  if (item.type === 'mc') return record.value === item.answerIndex;
  if (item.type === 'ox') return record.value === item.answer;
  return isShortAnswerCorrect(item, record.value);
};

const QuestionBody = ({ item, record, onAnswer, inputId }) => {
  const [draft, setDraft] = useState('');
  const answered = Boolean(record);

  if (item.type === 'mc') {
    return (
      <div className="wq-quiz__choices" role="group" aria-label="보기">
        {item.choices.map((choice, index) => {
          const picked = answered && record.value === index;
          const correct = answered && index === item.answerIndex;
          const state = correct ? 'is-correct' : (picked ? 'is-wrong' : '');
          return (
            <button
              aria-pressed={picked}
              className={`wq-quiz__choice ${state}`}
              disabled={answered}
              key={choice}
              onClick={() => onAnswer({ value: index })}
              type="button"
            >
              <span aria-hidden="true" className="wq-quiz__mark">{CHOICE_MARKS[index]}</span>
              <span className="wq-quiz__choice-text">{choice}</span>
              {correct && <span className="wq-quiz__sr">정답</span>}
              {picked && !correct && <span className="wq-quiz__sr">고른 답</span>}
            </button>
          );
        })}
      </div>
    );
  }

  if (item.type === 'ox') {
    return (
      <div className="wq-quiz__ox" role="group" aria-label="O 또는 X">
        {OX_OPTIONS.map((option) => {
          const picked = answered && record.value === option.value;
          const correct = answered && option.value === item.answer;
          const state = correct ? 'is-correct' : (picked ? 'is-wrong' : '');
          return (
            <button
              aria-label={`${option.label}, ${option.hint}`}
              aria-pressed={picked}
              className={`wq-quiz__ox-button ${state}`}
              disabled={answered}
              key={option.value}
              onClick={() => onAnswer({ value: option.value })}
              type="button"
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  }

  const submit = () => {
    if (!draft.trim() || answered) return;
    onAnswer({ value: draft.trim() });
  };
  return (
    <form
      className="wq-quiz__short"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label className="wq-quiz__sr" htmlFor={inputId}>답 쓰기</label>
      <input
        autoComplete="off"
        className="wq-quiz__input"
        disabled={answered}
        id={inputId}
        maxLength={80}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="답을 써 주세요"
        type="text"
        value={answered ? record.value : draft}
      />
      {!answered && (
        <button className="wq-quiz__submit" disabled={!draft.trim()} type="submit">확인</button>
      )}
    </form>
  );
};

const Feedback = ({ item, record, onOverride }) => {
  const correct = isCorrect(item, record);
  return (
    <div className={`wq-quiz__feedback ${correct ? 'is-correct' : 'is-wrong'}`}>
      <p className="wq-quiz__verdict">
        <span aria-hidden="true">{correct ? '✓' : '✕'}</span>
        {correct ? '정답이에요' : `아쉬워요. 정답은 ${answerLabel(item)}`}
      </p>
      {item.explain && <p className="wq-quiz__explain">{item.explain}</p>}
      {!correct && item.type === 'short' && (
        <button className="wq-quiz__override" onClick={onOverride} type="button">맞게 쓴 답이에요</button>
      )}
    </div>
  );
};

const QuizResult = ({ spec, records, onRetry }) => {
  const total = spec.items.length;
  const score = spec.items.filter((item, index) => isCorrect(item, records[index])).length;
  const message = score === total ? '모두 맞혔어요' : `${total}문제 중 ${score}문제 맞혔어요`;
  return (
    <div className="wq-quiz__result">
      <p className="wq-quiz__score" aria-live="polite">
        <strong>{score}</strong>
        <span> / {total}</span>
      </p>
      <p className="wq-quiz__score-text">{message}</p>
      <ol className="wq-quiz__review">
        {spec.items.map((item, index) => {
          const ok = isCorrect(item, records[index]);
          return (
            // eslint-disable-next-line react/no-array-index-key
            <li className={ok ? 'is-correct' : 'is-wrong'} key={index}>
              <span aria-label={ok ? '맞음' : '틀림'} className="wq-quiz__review-mark" role="img">{ok ? '✓' : '✕'}</span>
              <span className="wq-quiz__review-q">{item.q}</span>
              {!ok && <span className="wq-quiz__review-answer">정답 {answerLabel(item)}</span>}
            </li>
          );
        })}
      </ol>
      <button className="wq-quiz__primary" onClick={onRetry} type="button">다시 풀기</button>
    </div>
  );
};

/** ```wq-quiz 대화 속 퀴즈 — 한 문제씩 풀고 바로 정답·해설을 본 뒤 끝에 점수. */
const InchatQuiz = ({ code }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const parsed = useMemo(() => parseQuizSpec(code), [code]);
  const enabled = isInchatQuizEnabled();
  const [records, setRecords] = useState({});
  const [current, setCurrent] = useState(0);
  const [finished, setFinished] = useState(false);
  const trackedRef = useRef(false);
  const headingRef = useRef(null);
  const idRef = useRef(`wq-quiz-${Math.random().toString(36).slice(2, 9)}`);
  const copyMarkdown = useMemo(() => quizSpecToMarkdown(parsed.spec), [parsed.spec]);

  useEffect(() => {
    if (streaming || trackedRef.current || !isChatPage()) return;
    trackedRef.current = true;
    if (parsed.ok) {
      trackChatUxEvent('inchat_quiz_shown', {
        sourceCount: parsed.spec.items.length,
        stage: enabled ? 'quiz' : 'quiz_plain',
        errorCode: parsed.dropped ? 'partial' : 'none',
      });
    } else {
      trackChatUxEvent('inchat_quiz_invalid', { stage: 'quiz', errorCode: parsed.error });
    }
  }, [enabled, parsed, streaming]);

  if (streaming) {
    return <QuizPlainView markdown={quizSpecToMarkdown(parsed.spec, { withAnswers: false })} pending />;
  }
  if (!enabled || !parsed.ok) {
    return (
      <QuizPlainView
        copyMarkdown={parsed.spec.items.length ? copyMarkdown : ''}
        markdown={parsed.spec.items.length ? copyMarkdown : ''}
      />
    );
  }

  const { spec } = parsed;
  const total = spec.items.length;
  const item = spec.items[current];
  const record = records[current];

  const focusHeading = () => {
    requestAnimationFrame(() => headingRef.current?.focus({ preventScroll: true }));
  };
  const answer = (value) => {
    setRecords((prev) => (prev[current] ? prev : { ...prev, [current]: value }));
  };
  const override = () => {
    setRecords((prev) => ({ ...prev, [current]: { ...prev[current], override: true } }));
  };
  const next = () => {
    if (current + 1 < total) {
      setCurrent(current + 1);
    } else {
      setFinished(true);
      if (isChatPage()) {
        trackChatUxEvent('inchat_quiz_completed', {
          sourceCount: total,
          correctCount: spec.items.filter((it, index) => isCorrect(it, records[index])).length,
        });
      }
    }
    focusHeading();
  };
  const retry = () => {
    setRecords({});
    setCurrent(0);
    setFinished(false);
    if (isChatPage()) trackChatUxEvent('inchat_quiz_retry', { sourceCount: total });
    focusHeading();
  };

  return (
    <section aria-label={spec.title} className="wq-quiz">
      <div className="wq-quiz__head">
        <div className="wq-quiz__meta">
          <span className="wq-quiz__title">{spec.title}</span>
          <span className="wq-quiz__count">{total}문제 · 풀면 바로 채점돼요</span>
        </div>
        <QuizCopyButton kind="quiz" label={spec.title} markdown={copyMarkdown} />
      </div>
      {finished ? (
        <div className="wq-quiz__body">
          <h4 className="wq-quiz__sr" ref={headingRef} tabIndex={-1}>결과</h4>
          <QuizResult onRetry={retry} records={records} spec={spec} />
        </div>
      ) : (
        <div className="wq-quiz__body">
          <div
            aria-label={`${total}문제 중 ${current + 1}번째`}
            aria-valuemax={total}
            aria-valuemin={1}
            aria-valuenow={current + 1}
            className="wq-quiz__progress"
            role="progressbar"
          >
            <span style={{ width: `${((current + (record ? 1 : 0)) / total) * 100}%` }} />
          </div>
          <h4 className="wq-quiz__question" ref={headingRef} tabIndex={-1}>
            <span className="wq-quiz__number">{current + 1} / {total}</span>
            {item.q}
          </h4>
          <QuestionBody
            inputId={`${idRef.current}-${current}`}
            item={item}
            key={current}
            onAnswer={answer}
            record={record}
          />
          <div aria-live="polite">
            {record && <Feedback item={item} onOverride={override} record={record} />}
          </div>
          {record && (
            <button className="wq-quiz__primary" onClick={next} type="button">
              {current + 1 < total ? '다음 문제' : '결과 보기'}
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default InchatQuiz;
